import React, { useCallback, useEffect, useState } from "react";
import {
  AppointmentCardControlled,
  NHEAppointmentStatusEnum,
  NheDetails,
  NheStatus,
  SdaDetails,
} from "./card-controlled";
import {
  CSSSteps,
  JobScheduleDetails,
  SFAppStep,
} from "../manage-app-card/card-controlled";
import { useParams } from "react-router-dom";
import * as adobeAnalytics from "../../utility/adobe-analytics";

interface AppointmentCardUncontrolledProps {
  sdaAccessor: () => Promise<SdaDetails | undefined>;
  nheAccessor: () => Promise<NheDetails | undefined>;
  jcAccessor: () => Promise<JobScheduleDetails | undefined>;
  sfAccessor: () => Promise<NheStatus | undefined>;
  stepAccessor: () => Promise<SFAppStep | undefined>;
  cssURLWithoutScheduleId?: string;
}

export const AppointmentCardUncontrolled = ({
  jcAccessor,
  sdaAccessor,
  nheAccessor,
  sfAccessor,
  stepAccessor,
  cssURLWithoutScheduleId,
}: AppointmentCardUncontrolledProps) => {
  const { requisitionId, applicationId } = useParams<{
    requisitionId: string;
    applicationId: string;
  }>();
  const [sdaDetails, setSdaDetails] = useState<SdaDetails | undefined>();
  const [nheDetails, setNheDetails] = useState<NheDetails | undefined>();
  const [displayNHE, setDisplayNHE] = useState<boolean | undefined>();
  const [eligibleForCSS, setEligibleForCSS] = useState<boolean>(false);
  const [scheduleIdFound, setScheduleIdFound] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);

  const sendAnalyticsEvent = useCallback(
    async (eventName: string) => {
      const scheduleDetails = await jcAccessor();
      await adobeAnalytics.addEventMetric(window, applicationId, eventName, {
        dragonstoneSchedule: {
          scheduleID: scheduleDetails?.scheduleId,
          jobID: requisitionId,
        },
        geocluster: {
          ID: scheduleDetails?.geoClusterId,
        },
      });
    },
    [applicationId]
  );

  const trackStartDateDetailsClick = useCallback(
    () => sendAnalyticsEvent("first day what to expect click"),
    [sendAnalyticsEvent]
  );

  const trackNHEDetailsClick = useCallback(
    () => sendAnalyticsEvent("ash view appointment details"),
    [sendAnalyticsEvent]
  );

  const trackSupportDocClick = useCallback(
    () => sendAnalyticsEvent("ash list of documents"),
    [sendAnalyticsEvent]
  );

  const requestNheAndSda = useCallback(async () => {
    setLoading(true);
    try {
      const sdaDetails = await sdaAccessor();
      const nheDetails = await nheAccessor();
      const jcDetails = await jcAccessor();
      const sfDetails = await sfAccessor();
      const stepDetails = await stepAccessor();

      if (
        CSSSteps.includes(
          stepDetails?.sfApplicationStepSubStep
            ? stepDetails.sfApplicationStepSubStep
            : ""
        )
      ) {
        setEligibleForCSS(true);
      }

      if (jcDetails?.error) {
        if (jcDetails?.error?.errorCode === "APPLICATION_DETAILS_NOT_FOUND") {
          setScheduleIdFound(false);
        }
      }

      if (sdaDetails?.errorCode || nheDetails?.errorCode) {
        setError(true);
      } else {
        setSdaDetails(sdaDetails);

        if (nheDetails) {
          if (sfDetails?.status === NHEAppointmentStatusEnum.COMPLETED) {
            setDisplayNHE(false);
          } else {
            setDisplayNHE(true);
          }
        }
        setNheDetails(nheDetails);
        setError(false);
      }
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    requestNheAndSda();
  }, []);

  return (
    <AppointmentCardControlled
      onViewNHEDetailsClick={trackNHEDetailsClick}
      onViewStartDateDetailsClick={trackStartDateDetailsClick}
      onViewListOfDocsClick={trackSupportDocClick}
      sdaDetails={sdaDetails}
      nheDetails={nheDetails}
      displayNHE={displayNHE}
      scheduleIdFound={scheduleIdFound}
      cssURLWithoutScheduleId={cssURLWithoutScheduleId}
      onRetry={requestNheAndSda}
      loading={loading}
      error={error}
      eligibleForCSS={eligibleForCSS}
    />
  );
};
